import { useMemo, FC, useContext } from 'react';

import classnames from 'classnames';
import dynamic from 'next/dynamic';
import Head from 'next/head';

import AppContext, { IAppContext } from '~/contexts/AppContext';
import { ILayoutPropsModel } from './types';
import useLayoutStyles from './LayoutJSS';

const Header = dynamic(() => import('../Header/Header'));
const Footer = dynamic(() => import('../Footer/Footer'));

const Layout: FC<ILayoutPropsModel> = ({
  sidebarHorizontal,
  sidebarVertical,
  description,
  keywords,
  children,
  sidebar,
  title,
  top,
}) => {
  const {
    footerBottomOffset,
    top: topStyles,
    content,
    section,
    header,
    footer,
    root,
    main,
    row,
  } = useLayoutStyles();
  const { hasBottomOffset, baseTagHref } = useContext<IAppContext>(AppContext);

  const memoizedContentClassNames = useMemo<{ [key: string]: boolean }>(
    () => ({
      'with-sidebar': !!sidebar,
      'sidebar-top': !!sidebar && sidebarVertical === 'top',
      'sidebar-left': !!sidebar && sidebarHorizontal === 'left',
    }),
    [!!sidebar, sidebarVertical, sidebarHorizontal],
  );

  return (
    <>
      <Head>
        <meta httpEquiv="X-UA-Compatible" content="ie=edge" />
        <meta
          name="viewport"
          content="width=device-width, user-scalable=no, initial-scale=1.0, maximum-scale=1.0, minimum-scale=1.0"
        />
        <script
          src="https://optifyr.com/pulse/picsart/pulse.js?include=settings,tracker"
          async
        ></script>
        {description && <meta name="description" content={description} />}
        {keywords && <meta name="keywords" content={keywords} />}
        {baseTagHref && <base href={baseTagHref} />}
        {title && <title>{title}</title>}
      </Head>
      <div
        className={classnames(root, { [footerBottomOffset]: hasBottomOffset })}
      >
        <div className={row}>
          <div className={classnames(section, header)}>
            <Header />
          </div>
        </div>
        <div className={row}>
          <div className={classnames(section, 'mainContent')}>
            {top && <div className={topStyles}>{top}</div>}
            <div className={classnames(content, memoizedContentClassNames)}>
              <div className={main}>{children}</div>
              {!!sidebar && sidebar}
            </div>
          </div>
        </div>
        <div className={row}>
          <div className={classnames(section, footer)}>
            <Footer />
          </div>
        </div>
      </div>
    </>
  );
};

export default Layout;
